import React from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { nudgeDrawer } from "../../utils/modalAttention";
import styles from "./SideDrawer.module.css";

/**
 * SideDrawer — wysuwany panel z prawej krawędzi. Port 1:1 z mPracownika.
 *
 * DROP-IN ZAMIENNIK dla components/Modal/Modal: przyjmuje TE SAME propsy
 * (isOpen, onClose, title, children, width), więc zamiana modala na panel to podmiana importu —
 * formularz w środku zostaje nietknięty. Dodatkowo obsługuje `icon`, `subtitle` i `footer`.
 *
 * Uwaga na `width`: w modalu była to szerokość okna wyśrodkowanego (często 1000-1500px).
 * Tu jest to szerokość panelu przy krawędzi, więc wartość jest przycinana do rozsądnego zakresu
 * (min 380px, max 96vw) — inaczej "panel" zająłby cały ekran i nie różnił się od modala.
 *
 * Zamknięcie WYŁĄCZNIE przez X / przyciski w stopce. Klik w tło NIE zamyka — daje potrząśnięcie
 * (warunek W-10 design systemu: chroni przed utratą wpisanych danych).
 *
 * Akcje formularza zawsze przez prop `footer`, nigdy w `children` — inaczej stopka odjedzie
 * razem ze scrollem treści.
 *
 * `elevated` — panel otwierany Z WNĘTRZA okna modal (kreator wniosku → klauzula RODO).
 * Domyślna warstwa szuflad (1100) leży POD modalem (1250), więc bez tego panel otwierał się
 * niewidocznie za oknem: użytkownik klikał „Przeczytaj i zaakceptuj" i nic się nie działo.
 */
const SideDrawer = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    children,
    footer,
    width = "560px",
    elevated = false,
}) => {
    if (!isOpen) return null;

    // Modale bywały bardzo szerokie (np. 1500px na podglądzie faktury). Jako panel boczny
    // ograniczamy do 96vw, żeby zawsze było widać, że to wysuwana karta, a nie pełny ekran.
    const drawerStyle = {
        width,
        minWidth: "min(380px, 100vw)",
        maxWidth: "96vw",
    };

    // nudgeDrawer sam sprawdza, czy klik trafił w SAMO tło (e.target === e.currentTarget).
    return createPortal(
        <div
            className={`${styles.overlay} ${elevated ? styles.overlayElevated : ""}`}
            onClick={nudgeDrawer}
        >
            <aside
                className={`${styles.drawer} ${elevated ? styles.drawerElevated : ""}`}
                style={drawerStyle}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === "string" ? title : undefined}
            >
                <header className={styles.header}>
                    <div className={styles.headerLeft}>
                        {icon && <span className={styles.headerIcon}>{icon}</span>}
                        <div className={styles.headerTitles}>
                            <h2 className={styles.title}>{title}</h2>
                            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                        </div>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Zamknij">
                        <FiX size={17} />
                    </button>
                </header>

                <div className={styles.body}>{children}</div>

                {footer && <div className={styles.footer}>{footer}</div>}
            </aside>
        </div>,
        document.body
    );
};

export default SideDrawer;
